import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Button, message, Tabs, Switch, Tooltip, notification, Spin } from 'antd';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import moment from 'moment';
import PropTypes from 'prop-types';

import { FETCH_CATALOG_XML_LINKS } from 'graphql/queries/contentLibraryQueries';
import {
  DELETE_CATALOG_XML_LINK,
  SYNC_CATALOG_FROM_XML_BACKGROUND,
  SAVE_CATALOG_XML_LINK,
} from 'graphql/mutations/contentLibraryMutations';
import {
  DeleteIcon,
  CopyTOTPIcon,
  SyncIcon,
  QuarterProgressIcon,
  CloseIcon,
  PlusThinIcon,
} from 'components/Icons';
import { userVar } from 'graphql/cache';

import './CatalogSyncModal.scss';

const { TabPane } = Tabs;

/**
 * Catalog Sync Modal Component
 *
 * @param {Object} props
 * @param {boolean} props.visible - Whether modal is visible
 * @param {Function} props.onCancel - Cancel callback
 * @param {Function} props.refetchCatalogs - Refetch catalogs callback
 */
const CatalogSyncModal = ({
  visible,
  onCancel,
  setShowCatalogSyncModal,
  refetchCatalogs,
  onAddXmlLink,
}) => {
  const user = useReactiveVar(userVar);

  const [activeTabKey, setActiveTabKey] = useState('');
  const [syncingLinkId, setSyncingLinkId] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [showMinimizedIndicator, setShowMinimizedIndicator] = useState(true);
  // Pending changes that will be saved when Save button is clicked
  const [pendingAutoSyncChanges, setPendingAutoSyncChanges] = useState(new Map());
  const [pendingDeletions, setPendingDeletions] = useState(new Set());

  const prevSyncStatusesRef = useRef(new Map());

  // Fetch XML links with polling when sync is in progress
  // Continue fetching even when modal is closed if sync is active
  const { data, loading, refetch, startPolling, stopPolling } = useQuery(FETCH_CATALOG_XML_LINKS, {
    fetchPolicy: 'network-only',
    skip: false, // Always fetch to check sync status (needed for minimized indicator)
    pollInterval: 0, // Start with no polling, will be enabled when sync is active
  });

  const xmlLinks = useMemo(() => data?.fetchCatalogXMLLinks || [], [data]);

  // Mutations
  const [deleteXMLLink] = useMutation(DELETE_CATALOG_XML_LINK);
  const [syncXMLCatalogBackground] = useMutation(SYNC_CATALOG_FROM_XML_BACKGROUND);
  const [saveXMLLink] = useMutation(SAVE_CATALOG_XML_LINK);

  // Initialize prevSyncStatusesRef with current statuses to prevent notifications on mount
  useEffect(() => {
    if (xmlLinks.length > 0 && prevSyncStatusesRef.current.size === 0) {
      xmlLinks.forEach((link) => {
        if (link.syncStatus) {
          prevSyncStatusesRef.current.set(link._id, link.syncStatus);
        }
      });
    }
  }, [xmlLinks]);

  // Set active tab to first link when data loads
  useEffect(() => {
    if (xmlLinks.length > 0 && activeTabKey === '') {
      setActiveTabKey(xmlLinks[0]._id);
    }
  }, [xmlLinks, activeTabKey]);

  useEffect(() => {
    if (visible) {
      refetch();
      // Reset pending changes when modal opens
      setPendingAutoSyncChanges(new Map());
      setPendingDeletions(new Set());
    }
  }, [visible, refetch]);

  // Check for active syncs and start polling if needed
  useEffect(() => {
    const hasActiveSync = xmlLinks.some(
      (link) => link.syncStatus === 'in_progress' || link.syncStatus === 'pending'
    );

    if (hasActiveSync) {
      // Start polling every 2 seconds when sync is in progress (even if modal is closed)
      startPolling(2000);
      // Show minimized indicator if sync is active and modal is minimized or closed
      // This ensures indicator shows when user returns to page with active sync
      if (minimized || !visible) {
        setShowMinimizedIndicator(true);
      }
    } else {
      stopPolling();
      // Hide minimized indicator when no active syncs
      setShowMinimizedIndicator(false);
      setMinimized(false);
    }

    // Check for completed/failed syncs and show notifications
    xmlLinks.forEach((link) => {
      const prevStatus = prevSyncStatusesRef.current.get(link._id);
      const currentStatus = link.syncStatus;

      // Only notify on status change (not on every poll, and not if prevStatus is undefined on first load)
      // This prevents showing notifications for already-completed syncs on page refresh
      if (prevStatus !== undefined && prevStatus !== currentStatus) {
        prevSyncStatusesRef.current.set(link._id, currentStatus);

        if (currentStatus === 'completed') {
          notification.success({
            message: 'Catalog Sync Complete',
            description: 'Your catalog has been successfully synced from XML.',
            placement: 'topRight',
            duration: 5,
          });
          if (refetchCatalogs) {
            refetchCatalogs();
          }
          // Hide minimized indicator when sync completes
          setShowMinimizedIndicator(false);
          setMinimized(false);
        } else if (currentStatus === 'failed') {
          notification.error({
            message: 'Catalog Sync Failed',
            description: 'Failed to sync catalog from XML. Please try again.',
            placement: 'topRight',
            duration: 5,
          });
          // Hide minimized indicator when sync fails
          setShowMinimizedIndicator(false);
          setMinimized(false);
        }
      } else if (prevStatus === undefined && currentStatus) {
        // Initialize status in ref if it wasn't set before
        prevSyncStatusesRef.current.set(link._id, currentStatus);
      }
    });

    // Cleanup: only stop polling if there are no active syncs
    // Note: If component unmounts, polling will stop automatically, but sync continues on backend
    // When component remounts, it will check for active syncs and resume polling if needed
  }, [xmlLinks, visible, minimized, startPolling, stopPolling, refetchCatalogs]);

  /**
   * Handle delete XML link (mark for deletion, will be saved when Save button is clicked)
   *
   * @param {string} _id - XML link ID
   */
  const handleDeleteXMLLink = (_id) => {
    // Mark for deletion
    const newPendingDeletions = new Set(pendingDeletions).add(_id);
    setPendingDeletions(newPendingDeletions);

    // Remove any pending auto sync changes for this link
    setPendingAutoSyncChanges((prev) => {
      const newMap = new Map(prev);
      newMap.delete(_id);
      return newMap;
    });

    // Switch to first tab if current tab was marked for deletion
    if (activeTabKey === _id && xmlLinks.length > 1) {
      const remainingLinks = xmlLinks.filter(
        (link) => link._id !== _id && !newPendingDeletions.has(link._id)
      );
      if (remainingLinks.length > 0) {
        setActiveTabKey(remainingLinks[0]._id);
      }
    }
  };

  /**
   * Handle sync now (background)
   *
   * @param {string} _id - XML link ID
   */
  const handleSyncNow = async (_id) => {
    if (!_id) {
      message.error('Please select an XML link');
      return;
    }

    setSyncingLinkId(_id);
    try {
      await syncXMLCatalogBackground({
        variables: { _id },
        onCompleted: () => {
          // Clear syncing state immediately after mutation completes
          setSyncingLinkId(null);
          // Refetch to get updated sync status
          refetch();
        },
        onError: (error) => {
          setSyncingLinkId(null);
          message.error(error.message || 'Failed to start catalog sync');
        },
      });
      message.success('Catalog sync started. It will be completed shortly.');
      // Refetch to get initial sync status
      refetch();
      // Allow minimizing the modal - indicator will show automatically
    } catch (error) {
      setSyncingLinkId(null);
      message.error(error.message || 'Failed to start catalog sync');
    }
  };

  /**
   * Handle minimize modal
   */
  const handleMinimize = () => {
    setMinimized(true);
    setShowMinimizedIndicator(true);
    // Hide the modal when minimized
    if (onCancel) {
      onCancel();
    }
  };

  /**
   * Handle restore modal
   */
  const handleRestore = () => {
    setMinimized(false);
    // Show the modal when restored
    if (setShowCatalogSyncModal) {
      setShowCatalogSyncModal(true);
    }
  };

  /**
   * Handle toggle auto sync (store locally, will be saved when Save button is clicked)
   *
   * @param {Object} link - XML link object
   * @param {boolean} checked - New auto sync value
   */
  const handleToggleAutoSync = (link, checked) => {
    // Don't allow changes if link is marked for deletion
    if (pendingDeletions.has(link._id)) {
      return;
    }

    // If the new value matches the original saved value, remove from pending changes
    if (checked === link.autoSync) {
      setPendingAutoSyncChanges((prev) => {
        const newMap = new Map(prev);
        newMap.delete(link._id);
        return newMap;
      });
    } else {
      // Store pending change
      setPendingAutoSyncChanges((prev) => {
        const newMap = new Map(prev);
        newMap.set(link._id, checked);
        return newMap;
      });
    }
  };

  /**
   * Get effective auto sync value (original or pending change)
   *
   * @param {Object} link - XML link object
   * @returns {boolean} Effective auto sync value
   */
  const getEffectiveAutoSync = (link) => {
    if (pendingAutoSyncChanges.has(link._id)) {
      return pendingAutoSyncChanges.get(link._id);
    }
    return link.autoSync;
  };

  /**
   * Handle save - apply all pending changes (auto sync toggles and deletions)
   */
  const handleSave = async () => {
    try {
      // Apply all pending deletions
      const deletionPromises = Array.from(pendingDeletions).map((_id) =>
        deleteXMLLink({
          variables: { _id },
        })
      );

      // Apply all pending auto sync changes (skip links marked for deletion)
      const autoSyncPromises = Array.from(pendingAutoSyncChanges.entries())
        .filter(([_id]) => !pendingDeletions.has(_id)) // Don't update auto sync for links being deleted
        .map(([_id, autoSync]) => {
          const link = xmlLinks.find((l) => l._id === _id);
          if (!link) return Promise.resolve();

          return saveXMLLink({
            variables: {
              xmlLink: {
                _id: link._id,
                xmlUrl: link.xmlUrl,
                folderKey: link.folderKey,
                autoSync,
              },
            },
          });
        });

      // Execute all mutations
      await Promise.all([...deletionPromises, ...autoSyncPromises]);

      // Clear pending changes
      setPendingAutoSyncChanges(new Map());
      setPendingDeletions(new Set());

      message.success('Changes saved successfully');
      refetch();

      // Close modal
      if (onCancel) {
        onCancel();
      }
    } catch (error) {
      message.error(error.message || 'Failed to save changes');
    }
  };

  /**
   * Copy XML link to clipboard
   *
   * @param {string} xmlUrl - XML URL
   */
  const copyToClipboard = (xmlUrl) => {
    navigator.clipboard.writeText(xmlUrl);
    message.success('XML link copied to clipboard');
  };

  /**
   * Format date
   *
   * @param {string} date - Date string
   * @returns {string} Formatted date
   */
  const formatDate = (date, autoSync) => {
    if (!date || !autoSync) return 'Never';
    return moment(date).format(user?.profile?.dateFormat || 'MM/DD/YY');
  };

  /**
   * Get sync status text
   *
   * @param {Object} link - XML link object
   * @returns {string} Status text
   */
  const getSyncStatusText = (link) => {
    if (link.syncStatus === 'in_progress' || link.syncStatus === 'pending') {
      return 'Syncing...';
    }
    if (link.syncStatus === 'completed') {
      return 'Completed';
    }
    if (link.syncStatus === 'failed') {
      return 'Failed';
    }
    return formatDate(link.nextSyncDate);
  };

  // Check if any sync is in progress
  const hasActiveSync = xmlLinks.some(
    (link) => link.syncStatus === 'in_progress' || link.syncStatus === 'pending'
  );

  /**
   * Handle tab change
   *
   * @param {string} key - Tab key
   */
  const handleTabChange = (key) => {
    if (key === 'add-xml-link' && onAddXmlLink) {
      onAddXmlLink();
    } else {
      setActiveTabKey(key);
    }
  };

  const filteredXmlLinks = xmlLinks.filter((link) => !pendingDeletions.has(link._id));

  return (
    <>
      {/* Minimized indicator - shows when minimized or modal closed but sync active */}
      {(minimized || !visible) && hasActiveSync && showMinimizedIndicator && (
        <div className="catalog-sync-minimized">
          <div className="minimized-indicator">
            <Spin size="small" className="sync-loader" />
            <span className="sync-text">Catalog sync in progress...</span>
            <Button type="link" size="small" onClick={handleRestore}>
              View
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                // Hide the indicator but keep tracking (sync will continue)
                setShowMinimizedIndicator(false);
              }}>
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Main Modal */}
      <Modal
        className="catalog-sync-modal"
        title="Catalog Sync"
        visible={visible}
        onCancel={() => {
          // If sync is active, show minimized indicator when closing
          if (hasActiveSync) {
            setShowMinimizedIndicator(true);
          }
          if (onCancel) {
            onCancel();
          }
        }}
        footer={[
          <Button
            key="sync"
            type="link"
            icon={<SyncIcon spin={syncingLinkId === activeTabKey || hasActiveSync} />}
            onClick={() => handleSyncNow(activeTabKey)}
            loading={syncingLinkId === activeTabKey}
            disabled={
              syncingLinkId === activeTabKey ||
              hasActiveSync ||
              !activeTabKey ||
              filteredXmlLinks.length === 0
            }
            className="sync-now-btn">
            {syncingLinkId === activeTabKey
              ? 'Starting sync...'
              : hasActiveSync
              ? 'Sync in progress...'
              : 'Sync XML now'}
          </Button>,
          hasActiveSync && (
            <Button key="minimize" className="large-btn minimize-btn" onClick={handleMinimize}>
              Minimize
            </Button>
          ),
          !hasActiveSync && (
            <div key="footer-actions" className="modal-footer-buttons-wrapper">
              <Button
                key="cancel"
                className="large-btn cancel-btn"
                onClick={() => {
                  // Reset pending changes on cancel
                  setPendingAutoSyncChanges(new Map());
                  setPendingDeletions(new Set());
                  if (onCancel) {
                    onCancel();
                  }
                }}>
                Cancel
              </Button>
              {!hasActiveSync && (
                <Button
                  key="save"
                  type="primary"
                  className="large-btn secondary-btn"
                  onClick={handleSave}
                  disabled={pendingAutoSyncChanges.size === 0 && pendingDeletions.size === 0}>
                  Save
                </Button>
              )}
            </div>
          ),
        ].filter(Boolean)}
        width={500}
        closeIcon={<CloseIcon className="close-icon" onClick={onCancel} />}
        centered>
        {loading ? (
          <div>Loading...</div>
        ) : xmlLinks.length === 0 ? (
          <div className="empty-state">
            <p>No XML links saved yet.</p>
            <p className="empty-state-description">
              Import a catalog with the "Save XML link for future syncing" option to get started.
            </p>
            <span className="add-xml-link-tab-label" onClick={onAddXmlLink}>
              <PlusThinIcon />
              Add XML Link
            </span>
          </div>
        ) : (
          <Tabs
            activeKey={activeTabKey}
            onChange={handleTabChange}
            type="card"
            className="xml-links-tabs"
            tabBarExtraContent={
              <span className="add-xml-link-tab-label" onClick={onAddXmlLink}>
                <PlusThinIcon />
                Add XML Link
              </span>
            }>
            {filteredXmlLinks?.map((link, index) => {
              const effectiveAutoSync = getEffectiveAutoSync(link);
              return (
                <TabPane tab={`XML link #${index + 1}`} key={link._id}>
                  <div className="xml-link-content">
                    {/* XML URL */}
                    <div className="xml-url-section">
                      <span>Selected XML link:</span>
                      <Tooltip title={link.xmlUrl}>
                        <span className="xml-url">{link.xmlUrl}</span>
                      </Tooltip>
                      <CopyTOTPIcon
                        className="copy-icon"
                        onClick={() => copyToClipboard(link.xmlUrl)}
                      />
                      <DeleteIcon
                        className="delete-icon"
                        onClick={() => handleDeleteXMLLink(link._id)}
                      />
                    </div>

                    {/* Next Sync Date / Sync Status */}
                    <div className="sync-date-section">
                      <span>Auto-sync from XML</span>
                      <span>
                        {syncingLinkId === link._id ||
                        link.syncStatus === 'in_progress' ||
                        link.syncStatus === 'pending' ? (
                          <span>
                            <QuarterProgressIcon /> <strong>{getSyncStatusText(link)}</strong>
                          </span>
                        ) : null}
                      </span>
                    </div>

                    {/* Auto Sync Toggle */}
                    <div className="auto-sync-section">
                      <Switch
                        checked={effectiveAutoSync}
                        onChange={(checked) => handleToggleAutoSync(link, checked)}
                        disabled={syncingLinkId === link._id}
                      />
                      <span className="auto-sync-text">
                        {!effectiveAutoSync
                          ? 'This XML will not sync automatically'
                          : 'This XML syncs automatically once per day'}
                      </span>
                    </div>

                    {/* Last Sync Date */}
                    <div className="last-sync-section">
                      <span>Last sync:</span>
                      <span>{formatDate(link.lastSyncDate, effectiveAutoSync)}</span>
                    </div>
                  </div>
                </TabPane>
              );
            })}
          </Tabs>
        )}
      </Modal>
    </>
  );
};

/**
 * Default props
 */
CatalogSyncModal.defaultProps = {
  visible: false,
  onCancel: () => {},
  setShowCatalogSyncModal: null,
  refetchCatalogs: null,
  onAddXmlLink: null,
};

/**
 * Prop types
 */
CatalogSyncModal.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  setShowCatalogSyncModal: PropTypes.func,
  refetchCatalogs: PropTypes.func,
  onAddXmlLink: PropTypes.func,
};

export default CatalogSyncModal;
